import React, { useState, useEffect } from 'react';
import { Package, Plus, ChevronLeft, ChevronRight, Search, X, Edit, Trash2, Power, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';

// Predefined mock data for Warehouses
const initialWarehouses = [
  { id: 1, code: 'WH-MAIN', location: 'Building A - Zone 1', isActive: true },
  { id: 2, code: 'WH-SEC-01', location: 'Building B - Shelf 4', isActive: true },
  { id: 3, code: 'WH-HYD', location: 'Building A - Zone 3', isActive: true },
  { id: 4, code: 'WH-ELEC', location: 'Building C - Cabinet 2', isActive: true },
  { id: 5, code: 'WH-EXT-01', location: 'External Yard - Section B', isActive: false },
  { id: 6, code: 'WH-SCRAP', location: 'Building D - Zone 2', isActive: false }
];

export const WarehouseTable = ({ warehouses = [] }) => {
  // Search & filter states

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const pageSizeOptions = [5, 10, 20];

  // Selection state
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(null);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);

  // Custom toast notification state
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter]);

  // Handler: Toggle active status
  const handleToggleStatus = (id) => {
    setWarehouses(prev => prev.map(wh => {
      if (wh.id === id) {
        const nextState = !wh.isActive;
        showToast(`Warehouse ${wh.code} is now ${nextState ? 'Active' : 'Inactive'} (In-Memory)`, 'info');
        return { ...wh, isActive: nextState };
      }
      return wh;
    }));
  };

  // Handler: Delete warehouse (Mock)
  const handleDeleteMock = (id) => {
    const target = warehouses.find(wh => wh.id === id);
    if (target) {
      setWarehouses(prev => prev.filter(wh => wh.id !== id));
      showToast(`Warehouse ${target.code} deleted (In-Memory)`, 'info');
      if (selectedWarehouseId === id) {
        setSelectedWarehouseId(null);
      }
    }
  };

  // Handler: Save (Create / Edit)
  const handleSaveWarehouse = (data) => {
    if (data.id && warehouses.some(w => w.id === data.id)) {
      // Edit mode
      setWarehouses(prev => prev.map(w => w.id === data.id ? data : w));
      showToast(`Warehouse ${data.code} updated successfully (In-Memory)`, 'success');
    } else {
      // Create mode
      const newWh = {
        ...data,
        id: Date.now()
      };
      setWarehouses(prev => [...prev, newWh]);
      showToast(`Warehouse ${data.code} created successfully (In-Memory)`, 'success');
    }
    setIsModalOpen(false);
    setEditingWarehouse(null);
  };

  // Filtered warehouses based on search query & status filter
  const filteredWarehouses = warehouses.filter(wh => {
    const matchesSearch =
      (wh.code || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (wh.location || '').toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && wh.isActive) ||
      (statusFilter === 'inactive' && !wh.isActive);

    return matchesSearch && matchesStatus;
  });

  // Pagination calculation
  const totalItems = filteredWarehouses.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const activePage = Math.min(page, totalPages);

  const rangeStart = totalItems === 0 ? 0 : (activePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(activePage * pageSize, totalItems);

  const currentWarehouses = filteredWarehouses.slice(
    (activePage - 1) * pageSize,
    activePage * pageSize
  );

  return (
    <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg w-full min-w-0 min-h-[420px] flex flex-col relative font-sans">
      {/* Toast Notification */}
      {toast && (
        <div className="absolute top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-xl border bg-gray-900 border-gray-700 text-white animate-fade-in duration-300">
          {toast.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 text-blue-400 shrink-0" />
          )}
          <span className="text-sm font-medium">{toast.message}</span>
          <button onClick={() => setToast(null)} className="ml-2 text-gray-400 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header section with Title, badge and Create button */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-700 shrink-0">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Package className="w-5 h-5 text-gray-400 shrink-0" />
            <h2 className="text-xl font-semibold text-white">
              Warehouses
            </h2>
            <Badge className="bg-blue-900/50 text-blue-200">
              {totalItems} {totalItems === 1 ? 'warehouse' : 'warehouses'}
            </Badge>
          </div>
          <button
            type="button"
            onClick={() => {
              setEditingWarehouse(null);
              setIsModalOpen(true);
            }}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-green-600 bg-green-600 text-white text-sm font-medium hover:bg-green-700 hover:border-green-700 transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            Create New Warehouse
          </button>
        </div>

        {/* Inline Filters Bar */}
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="relative w-full sm:flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-sm text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
          <div className="w-full sm:w-auto flex items-center gap-2">
            <span className="text-xs text-gray-400 font-medium whitespace-nowrap">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-sm text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="p-4 sm:p-6 w-full min-w-0 flex-1 flex flex-col">
        {currentWarehouses.length === 0 ? (
          <div className="text-center py-16 flex-1 flex flex-col justify-center items-center">
            <Package className="w-16 h-16 text-gray-600 mb-4 animate-pulse" />
            <p className="text-gray-400 text-lg font-medium">
              {totalItems === 0 && searchQuery === ''
                ? 'No warehouses registered.'
                : 'No warehouses match your filters.'}
            </p>
          </div>
        ) : (
          <>
            <div className="w-full flex-1 min-h-0 overflow-y-auto">
              {/* Mobile Card Grid View */}
              <div className="grid grid-cols-1 gap-4 sm:hidden">
                {currentWarehouses.map((wh) => (
                  <div
                    key={wh.ID}
                    onClick={() => setSelectedWarehouseId(wh.ID)}
                    className={`bg-gray-800 border border-gray-700 rounded-lg p-4 shadow-sm hover:bg-gray-750 transition-all cursor-pointer ${selectedWarehouseId === wh.id ? 'ring-2 ring-blue-500 ring-inset' : ''
                      }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-semibold text-white">{wh.Code}</h3>
                      <Badge className={wh.isActive ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'}>
                        {wh.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 font-medium min-w-[80px]">Location:</span>
                        <span className="text-sm text-gray-300">{wh.Location}</span>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-2 border-t border-gray-700" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => handleToggleStatus(wh.ID)}
                        className="inline-flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300 transition-colors"
                      >
                        <Power className="w-3.5 h-3.5" /> Toggle
                      </button>
                      <button
                        onClick={() => {
                          setEditingWarehouse(wh);
                          setIsModalOpen(true);
                        }}
                        className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        <Edit className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteMock(wh.ID)}
                        className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block w-full">
                <div className="inline-block w-full align-middle">
                  <div className="shadow ring-1 ring-gray-700 rounded-lg w-full">
                    <table className="w-full divide-y divide-gray-700">
                      <thead className="bg-gray-700/50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Code
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Location
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-gray-800 divide-y divide-gray-700">
                        {currentWarehouses.map((wh, idx) => (
                          <tr
                            key={wh.ID}
                            onClick={() => setSelectedWarehouseId(wh.ID)}
                            className={`transition-all cursor-pointer ${idx % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'
                              } hover:bg-gray-700/70 ${selectedWarehouseId === wh.ID ? 'ring-2 ring-blue-500 ring-inset bg-gray-700/50' : ''
                              }`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-white">
                              {wh.Code}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                              {wh.Location}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <Badge className={wh.isActive ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'}>
                                {wh.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-400">
                              <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => handleToggleStatus(wh.ID)}
                                  title="Toggle active status (In-Memory)"
                                  className="p-1.5 rounded hover:bg-gray-600 hover:text-white transition-colors animate-all"
                                >
                                  <Power className="w-4 h-4 text-orange-400 hover:text-orange-300" />
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingWarehouse(wh);
                                    setIsModalOpen(true);
                                  }}
                                  title="Edit (In-Memory)"
                                  className="p-1.5 rounded hover:bg-gray-600 hover:text-white transition-colors"
                                >
                                  <Edit className="w-4 h-4 text-blue-400 hover:text-blue-300" />
                                </button>
                                <button
                                  onClick={() => handleDeleteMock(wh.ID)}
                                  title="Delete (In-Memory)"
                                  className="p-1.5 rounded hover:bg-gray-600 hover:text-red-400 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4 text-red-400 hover:text-red-300" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Pagination Controls */}
            {totalItems > 0 && (
              <div className="px-4 sm:px-6 py-4 border-t border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0 mt-auto">
                <div className="flex items-center gap-4 flex-wrap">
                  <p className="text-sm text-gray-500">
                    Showing {rangeStart}–{rangeEnd} of {totalItems} warehouses
                  </p>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-400">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="px-3 py-1.5 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    >
                      {pageSizeOptions.map((size) => (
                        <option key={size} value={size} className="bg-gray-800">
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={activePage <= 1}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <span className="text-sm text-gray-500 px-2">
                    Page {activePage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={activePage >= totalPages}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create / Edit Warehouse Modal (In-Memory Draft) */}
      <WarehouseModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingWarehouse(null);
        }}
        warehouse={editingWarehouse}
        onSave={handleSaveWarehouse}
      />
    </div>
  );
};

// Internal Modal component for creating/editing warehouses
const WarehouseModal = ({ isOpen, onClose, warehouse, onSave }) => {
  const [formData, setFormData] = useState({
    code: '',
    location: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (warehouse) {
        setFormData({
          id: warehouse.id,
          code: warehouse.code,
          location: warehouse.location,
          isActive: warehouse.isActive
        });
      } else {
        setFormData({
          code: '',
          location: '',
          isActive: true
        });
      }
      setErrors({});
    }
  }, [isOpen, warehouse]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.code.trim()) newErrors.code = 'Warehouse Code is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal Dialog Card */}
      <div className="relative bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all duration-300 scale-100 flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between shrink-0">
          <h3 className="text-lg font-semibold text-white">
            {warehouse ? 'Edit Warehouse (In-Memory)' : 'Create New Warehouse'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {/* Warehouse Code */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Warehouse Code <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g., WH-MAIN"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.code ? 'border-red-500' : 'border-gray-600'
                  }`}
              />
              {errors.code && <p className="mt-1 text-xs text-red-400">{errors.code}</p>}
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Location <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g., Building A - Zone 1"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.location ? 'border-red-500' : 'border-gray-600'
                  }`}
              />
              {errors.location && <p className="mt-1 text-xs text-red-400">{errors.location}</p>}
            </div>

            {/* Active Switch */}
            <div className="flex items-center gap-3 pt-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 rounded text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500 focus:ring-offset-gray-800 cursor-pointer"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-gray-300 cursor-pointer select-none">
                Mark as Active
              </label>
            </div>
          </div>

          {/* Action buttons */}
          <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-300 text-sm font-medium rounded-lg transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};